import { useEffect, useState, useRef, useMemo } from "react";
import "./user.css";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL || "https://api.transtrackglobal.in";

/* ================= IMPORT HELPERS ================= */
const HEADER_MAP = {
  client: ["client", "customer"],
  resourceName: ["resource name", "name of the resource", "resource"],
  recruiterName: ["recruiter", "recruiter name", "name of the recruiter"],
  gender: ["gender", "sex"],
  dob: ["dob", "date of birth"],
  email: ["email", "email id"],
  mobile: ["mobile", "mobile number", "phone", "mobile no"],
  dojTapl: ["doj tapl", "doj"],
  dojE: ["doj e", "doj client"],
  domain: ["domain"],
  location: ["location", "city", "base location", "work location"],
  lineManager: ["line manager", "manager"],
  lmEmail: ["lm email", "manager email"],
  lmMobile: ["lm mobile", "manager mobile"],
  jobStage: ["job stage", "stage"],
  jobRole: ["job role", "role"],
  rateCard: ["rate card", "ratecard"],
  ctc: ["ctc", "cost to company"],
  lastWorkingDay: ["last working day", "lwd", "last working date", "lwd date", "last working"],
  woNo: ["wo no", "work order"],
  woRenewalDate: ["wo renewal date"],
};

const normalizeKey = (key) =>
  key?.toString().trim().toLowerCase().replace(/\s+/g, " ");

const mapExcelRow = (row) => {
  const mapped = {};
  Object.entries(HEADER_MAP).forEach(([field, aliases]) => {
    const matchKey = Object.keys(row).find((k) =>
      aliases.includes(normalizeKey(k)),
    );
    if (matchKey) mapped[field] = row[matchKey];
  });
  return mapped;
};

const getRowErrors = (row) => {
  const errors = [];
  if (!row.resourceName) errors.push("Resource Name missing");
  if (!Number(row.rateCard) || Number(row.rateCard) <= 0)
    errors.push("Rate Card must be > 0");
  if (!Number(row.ctc) || Number(row.ctc) <= 0)
    errors.push("CTC must be > 0");
  return errors;
};

const TooltipCell = ({ value, limit = 12 }) => {
  if (!value) return <td>-</td>;
  const display = value.length > limit ? value.slice(0, limit) + "…" : value;
  return (
    <td>
      <span
        data-bs-toggle="tooltip"
        data-bs-placement="top"
        title={value}
        style={{ cursor: "pointer" }}
      >
        {display}
      </span>
    </td>
  );
};

const User = () => {
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "active";
  const userType = localStorage.getItem("userType") || "admin";
  
  const [globalSearch, setGlobalSearch] = useState("");
  
  const fileInputRef = useRef(null);
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [deleteModalUser, setDeleteModalUser] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 25;

  useEffect(() => {
    setCurrentPage(1);
    const fetchData = async () => {
      try {
        let response;
        if (filter === "renewal30") {
          response = await axios.get(`${API_URL}/api/renewaldetails`);
        } else {
          response = await axios.get(`${API_URL}/api/getAllUsers?status=${filter}`);
        }
        setUsers(response.data);
      } catch (error) {
        console.log("Error while fetching data", error);
      }
    };
    fetchData();
  }, [filter]);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]',
    );
    tooltipTriggerList.forEach((el) => {
      if (!el._tooltip) {
        el._tooltip = new window.bootstrap.Tooltip(el);
      }
    });
    return () => {
      tooltipTriggerList.forEach((el) => {
        if (el._tooltip) {
          el._tooltip.dispose();
          el._tooltip = null;
        }
      });
    };
  }, [users, currentPage, globalSearch]);

  const deleteUser = async (userId) => {
    try {
      const res = await axios.delete(`${API_URL}/api/delete/${userId}`);
      setUsers((prev) => prev.filter((u) => u._id !== userId));
      toast.success(res.data.message);
      setDeleteModalUser(null);
    } catch (error) {
      console.log(error);
      toast.error("Failed to delete record");
    }
  };

  const deletePermanentUser = async (userId) => {
    if (window.confirm("Are you sure? It will be cleared from database and cannot be undone.")) {
      try {
        const res = await axios.delete(`${API_URL}/api/deletePermanent/${userId}`);
        setUsers((prev) => prev.filter((u) => u._id !== userId));
        toast.success(res.data.message);
        setDeleteModalUser(null);
      } catch (error) {
        console.log(error);
        toast.error("Failed to permanently delete record");
      }
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/bulkDeletePermanent`, { ids: selectedIds });
      setUsers((prev) => prev.filter((u) => !selectedIds.includes(u._id)));
      setSelectedIds([]);
      toast.success(res.data.message);
      setShowBulkDeleteModal(false);
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || "Failed to permanently delete records");
      setShowBulkDeleteModal(false);
    }
  };

  // Filter users based on global search
  const filteredUsers = useMemo(() => {
    if (!globalSearch.trim()) return users;
    const lowerSearch = globalSearch.toLowerCase();
    return users.filter((u) => {
      return (
        (u.resourceName || "").toLowerCase().includes(lowerSearch) ||
        (u.email || "").toLowerCase().includes(lowerSearch) ||
        (u.client || "").toLowerCase().includes(lowerSearch) ||
        (u.domain || "").toLowerCase().includes(lowerSearch) ||
        (u.recruiterName || "").toLowerCase().includes(lowerSearch) ||
        (u.jobRole || "").toLowerCase().includes(lowerSearch)
      );
    });
  }, [users, globalSearch]);

  const handleRowSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredUsers.map((u) => u._id));
    }
    setSelectAll(!selectAll);
  };

  const getExportData = () => {
    return selectedIds.length > 0
      ? users.filter((u) => selectedIds.includes(u._id))
      : filteredUsers; // export visible (searched) if nothing selected
  };

  const exportToExcel = () => {
    const dataToExport = getExportData();

    const formattedData = dataToExport.map((u, i) => ({
      "Sl No": i + 1,
      Client: u.client || "",
      "Resource Name": u.resourceName || "",
      "Recruiter Name": u.recruiterName || "",
      Gender: u.gender || "",
      DOB: u.dob ? new Date(u.dob).toLocaleDateString("en-GB") : "",
      Email: u.email || "",
      Mobile: u.mobile || "",
      "DOJ TAPL": u.dojTapl ? new Date(u.dojTapl).toLocaleDateString("en-GB") : "",
      "DOJ Client": u.dojE ? new Date(u.dojE).toLocaleDateString("en-GB") : "",
      Domain: u.domain || "",
      Location: u.location || "",
      "Line Manager": u.lineManager || "",
      "LM Email": u.lmEmail || "",
      "LM Mobile": u.lmMobile || "",
      "Job Stage": u.jobStage || "",
      "Job Role": u.jobRole || "",
      "Rate Card": u.rateCard ?? "",
      CTC: u.ctc ?? "",
      ...(userType !== "hr manager" ? {
        Margin: u.margin ?? "",
        "Margin %": u.marginPercentage ? `${u.marginPercentage.toFixed(2)}%` : "",
      } : {}),
      Aadhar: u.aadhar || "",
      PAN: u.pan || "",
      "Reference Name 1": u.refName1 || "",
      "Reference Name 2": u.refName2 || "",
      "Reference Mobile 1": u.refMobile1 || "",
      "Reference Mobile 2": u.refMobile2 || "",
      "WO No": u.woNo || "",
      "WO Renewal Date": u.woRenewalDate ? new Date(u.woRenewalDate).toLocaleDateString("en-GB") : "",
      "Last Working Day": u.lastWorkingDay ? new Date(u.lastWorkingDay).toLocaleDateString("en-GB") : "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(workbook, "users.xlsx");
  };

  const ImportExcel = () => {
    fileInputRef.current.click();
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const buffer = evt.target.result;
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const rawData = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const normalizedData = rawData.map(mapExcelRow);
      setPreviewData(normalizedData);
      setShowPreview(true);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = null;
  };

  const filterLabelMap = {
    active: "Active Resources",
    inactive: "Inactive Resources",
    pipeline: "Pipeline Resources",
    renewal30: "WO Expiring This Month",
    lastworking30: "Last working days",
    deleted: "Deleted Resources",
    all: "All Resources",
    lastworking: "Last working Days(next 30days)",
  };

  const activeFilterLabel = filterLabelMap[filter] || "Active Resources";
  
  const MONTHS_MAP = {
    jan: 0, january: 0,
    feb: 1, february: 1,
    mar: 2, march: 2,
    apr: 3, april: 3,
    may: 4,
    jun: 5, june: 5,
    jul: 6, july: 6,
    aug: 7, august: 7,
    sep: 8, sept: 8, september: 8,
    oct: 9, october: 9,
    nov: 10, november: 10,
    dec: 11, december: 11
  };

  const normalizeYear = (y) => {
    if (y < 100) {
      return y >= 70 ? 1900 + y : 2000 + y;
    }
    return y;
  };

  const formatExcelDate = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (typeof value === "number") {
      if (value <= 0) return "";
      const d = new Date(Date.UTC(1970, 0, 1) + Math.round((value - 25569) * 86400 * 1000));
      return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return "";

      if (/^\d{4,6}$/.test(trimmed)) {
        const numVal = parseInt(trimmed, 10);
        if (numVal > 1000 && numVal < 200000) {
          const d = new Date(Date.UTC(1970, 0, 1) + Math.round((numVal - 25569) * 86400 * 1000));
          return `${String(d.getUTCDate()).padStart(2, "0")}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${d.getUTCFullYear()}`;
        }
      }
      
      const p1 = /(\d{1,2})(?:st|nd|rd|th)?(?:\s+of)?[\s,.-]+([A-Za-z]+)[\s,.-]+(\d{2,4})/i.exec(trimmed);
      if (p1) {
        const day = parseInt(p1[1], 10);
        const mStr = p1[2].toLowerCase();
        const year = normalizeYear(parseInt(p1[3], 10));
        if (MONTHS_MAP[mStr] !== undefined && day >= 1 && day <= 31) {
          return `${String(day).padStart(2, "0")}/${String(MONTHS_MAP[mStr] + 1).padStart(2, "0")}/${year}`;
        }
      }

      const p2 = /([A-Za-z]+)[\s,.-]+(\d{1,2})(?:st|nd|rd|th)?(?:[\s,.-]+(?:of\s+)?)?(\d{2,4})/i.exec(trimmed);
      if (p2) {
        const mStr = p2[1].toLowerCase();
        const day = parseInt(p2[2], 10);
        const year = normalizeYear(parseInt(p2[3], 10));
        if (MONTHS_MAP[mStr] !== undefined && day >= 1 && day <= 31) {
          return `${String(day).padStart(2, "0")}/${String(MONTHS_MAP[mStr] + 1).padStart(2, "0")}/${year}`;
        }
      }

      const p3 = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})/.exec(trimmed);
      if (p3) {
        const year = p3[1];
        const month = String(parseInt(p3[2], 10)).padStart(2, "0");
        const day = String(parseInt(p3[3], 10)).padStart(2, "0");
        return `${day}/${month}/${year}`;
      }

      const p4 = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})/.exec(trimmed);
      if (p4) {
        let part1 = parseInt(p4[1], 10);
        let part2 = parseInt(p4[2], 10);
        const year = normalizeYear(parseInt(p4[3], 10));
        let day = part1;
        let month = part2;
        if (part1 <= 12 && part2 > 12) {
          month = part1;
          day = part2;
        }
        return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
      }

      const nativeDate = new Date(trimmed);
      if (!isNaN(nativeDate.getTime())) {
        return `${String(nativeDate.getDate()).padStart(2, "0")}/${String(nativeDate.getMonth() + 1).padStart(2, "0")}/${nativeDate.getFullYear()}`;
      }
    }
    return String(value);
  };

  const formatDateOrBlank = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    if (typeof date === "string" && (date.endsWith("Z") || date.includes("T00:00:00") || date.includes("T18:30:00"))) {
      const dd = String(d.getUTCDate()).padStart(2, "0");
      const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
      const yyyy = d.getUTCFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const confirmImport = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/import-excel`, {
        data: previewData,
      });
      toast.success(res.data.message);
      
      const refreshed = await axios.get(`${API_URL}/api/getAllUsers?status=${filter}`);
      setUsers(refreshed.data);
      
      setPreviewData([]);
      setShowPreview(false);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.message) {
        toast.error(`Excel import failed: ${err.response.data.message}`);
      } else {
        toast.error("Excel import failed");
      }
    }
  };

  const invalidCount = previewData.filter((row) => getRowErrors(row).length > 0).length;

  const downloadTemplate = () => {
    const template = [
      {
        Client: "",
        "Resource Name": "",
        "Recruiter Name": "",
        Gender: "",
        DOB: "",
        Email: "",
        Mobile: "",
        "DOJ TAPL": "",
        "DOJ Client": "",
        Domain: "",
        Location: "",
        "Line Manager": "",
        "LM Email": "",
        "LM Mobile": "",
        "Job Stage": "",
        "Job Role": "",
        "Rate Card": "",
        CTC: "",
        MonthlyCTC: "",
        Margin: "",
        "Margin %": "",
        Aadhar: "",
        PAN: "",
        "Reference Name 1": "",
        "Reference Name 2": "",
        "Reference Mobile 1": "",
        "Reference Mobile 2": "",
        "WO No": "",
        "WO Renewal Date": "",
        "Last Working Day": "",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "Resource_Import_Template.xlsx");
  };

  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredUsers.slice(indexOfFirstRow, indexOfLastRow);

  return (
    <div className="user-page">
      {/* HEADER */}
      <div className="user-header">
        <div className="d-flex flex-column gap-2">
          <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
            <i className="bi bi-people-fill text-primary"></i> Manage Records
          </h5>
          <div className="active-filter-badge">
            <i className="bi bi-funnel-fill text-secondary me-1"></i>
            <span className="text-secondary">Applied Filter: </span>
            <span className="fw-medium">{activeFilterLabel}</span>
          </div>
        </div>
        
        {/* GLOBAL SEARCH */}
        <div className="global-search-container">
          <div className="search-input-wrapper">
            <i className="bi bi-search search-icon"></i>
            <input 
              type="text" 
              className="form-control" 
              placeholder="Search by Name, Email, Client, Domain..." 
              value={globalSearch}
              onChange={(e) => {
                setGlobalSearch(e.target.value);
                setCurrentPage(1);
              }}
            />
            {globalSearch && (
              <i className="bi bi-x-circle-fill clear-search" onClick={() => setGlobalSearch("")}></i>
            )}
          </div>
        </div>

        <div className="user-actions">
          <button onClick={ImportExcel} className="btn outline-btn text-nowrap d-inline-flex align-items-center">
            <i className="bi bi-file-earmark-arrow-up me-2"></i> Import
            <i
              className="bi bi-info-circle ms-2"
              title="Download Template"
              onClick={(e) => {
                e.stopPropagation();
                downloadTemplate();
              }}
              style={{ cursor: "pointer" }}
            />
          </button>
          <input type="file" accept=".xlsx,.xls" ref={fileInputRef} style={{ display: "none" }} onChange={handleExcelUpload} />

          <button onClick={exportToExcel} className="btn outline-btn text-nowrap d-inline-flex align-items-center">
            <i className="bi bi-file-earmark-arrow-down me-2"></i> Export
          </button>

          {(userType === "admin" || userType === "superadmin") && selectedIds.length > 0 && (
            <button onClick={() => setShowBulkDeleteModal(true)} className="btn danger-btn text-nowrap d-inline-flex align-items-center">
              <i className="bi bi-trash3-fill me-2"></i> Bulk Delete
            </button>
          )}
          
          <Link to="/add" className="btn gradient-btn text-nowrap d-inline-flex align-items-center">
            <i className="bi bi-person-plus-fill me-2"></i> Add Record
          </Link>

          {/* FILTER DROPDOWN */}
          <div className="dropdown">
            <button className="btn outline-btn dropdown-toggle" type="button" data-bs-toggle="dropdown">
              <i className="bi bi-funnel me-1"></i> Filter
            </button>
            <ul className="dropdown-menu dropdown-menu-end shadow">
              <li><button className="dropdown-item" onClick={() => setSearchParams({ filter: "active" })}><i className="bi bi-check-circle text-success me-2"></i>Active Resources</button></li>
              <li><button className="dropdown-item" onClick={() => setSearchParams({ filter: "inactive" })}><i className="bi bi-x-circle text-warning me-2"></i>Inactive Resources</button></li>
              <li><button className="dropdown-item" onClick={() => setSearchParams({ filter: "pipeline" })}><i className="bi bi-person-lines-fill text-primary me-2"></i>Pipeline Resources</button></li>
              <li><button className="dropdown-item" onClick={() => setSearchParams({ filter: "renewal30" })}><i className="bi bi-calendar-event text-info me-2"></i>WO Expiring This Month</button></li>
              <li><button className="dropdown-item" onClick={() => setSearchParams({ filter: "deleted" })}><i className="bi bi-trash text-danger me-2"></i>Deleted Resources</button></li>
              <li><button className="dropdown-item" onClick={() => setSearchParams({ filter: "lastworking30" })}><i className="bi bi-clock-history text-secondary me-2"></i>LWD (next 30days)</button></li>
              <li><hr className="dropdown-divider" /></li>
              <li><button className="dropdown-item" onClick={() => setSearchParams({ filter: "all" })}><i className="bi bi-list me-2"></i>All Resources</button></li>
            </ul>
          </div>
        </div>
      </div>

      {/* TABLE */}
      {filteredUsers.length === 0 ? (
        <div className="no-data-modern">
          <div className="no-data-icon">
            <i className="bi bi-search"></i>
          </div>
          <h6>No matching records found</h6>
          <p className="text-muted">Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="table-scroll-wrapper">
          <table className="resource-table main-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                </th>
                <th>Sl No.</th>
                <th>Client</th>
                <th>Resource Name</th>
                <th>Recruiter</th>
                <th>Gender</th>
                <th>DOB</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>DOJ TAPL</th>
                <th>DOJ Client</th>
                <th>Domain</th>
                <th>Location</th>
                <th>Manager</th>
                <th>LM Email</th>
                <th>LM Mobile</th>
                <th>Stage</th>
                <th>Role</th>
                <th>Rate Card</th>
                <th>CTC</th>
                <th>Monthly CTC</th>
                {userType !== "hr manager" && (
                  <>
                    <th>Margin</th>
                    <th>Margin %</th>
                  </>
                )}
                <th>Aadhar</th>
                <th>PAN</th>
                <th>Ref 1</th>
                <th>Ref 2</th>
                <th>Ref1 Mob</th>
                <th>Ref2 Mob</th>
                <th>WO No</th>
                <th>WO Renewal</th>
                <th>Last Working Day</th>
                <th className="sticky-action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.map((user, index) => (
                <tr key={user._id}>
                  <td className="checkbox-col">
                    <input type="checkbox" checked={selectedIds.includes(user._id)} onChange={() => handleRowSelect(user._id)} />
                  </td>
                  <td>{indexOfFirstRow + index + 1}</td>
                  <TooltipCell value={user.client} />
                  <TooltipCell value={user.resourceName} />
                  <TooltipCell value={user.recruiterName} />
                  <td>{user.gender}</td>
                  <td>{formatDateOrBlank(user.dob)}</td>
                  <TooltipCell value={user.email} limit={18} />
                  <td>{user.mobile}</td>
                  <td>{formatDateOrBlank(user.dojTapl)}</td>
                  <td>{formatDateOrBlank(user.dojE)}</td>
                  <td>{user.domain}</td>
                  <td>{user.location}</td>
                  <td>{user.lineManager}</td>
                  <td>{user.lmEmail}</td>
                  <td>{user.lmMobile}</td>
                  <td>{user.jobStage}</td>
                  <td>{user.jobRole}</td>
                  <td>{user.rateCard}</td>
                  <td>{user.ctc}</td>
                  <td>{user.monthlyctc}</td>
                  {userType !== "hr manager" && (
                    <>
                      <td>{user.margin ?? "-"}</td>
                      <td>{user.marginPercentage?.toFixed(2) ?? "-"}</td>
                    </>
                  )}
                  <td>{user.aadhar}</td>
                  <td>{user.pan}</td>
                  <td>{user.refName1}</td>
                  <td>{user.refName2}</td>
                  <td>{user.refMobile1}</td>
                  <td>{user.refMobile2}</td>
                  <td>{user.woNo}</td>
                  <td>{formatDateOrBlank(user.woRenewalDate)}</td>
                  <td>
                    {user.lastWorkingDay ? (
                      <span className="badge bg-light text-dark border">
                        {new Date(user.lastWorkingDay).toLocaleDateString("en-GB")}
                      </span>
                    ) : "-"}
                  </td>
                  <td className="sticky-action-col">
                    <div className="action-buttons-group">
                      {filter !== "deleted" && (
                        <Link to={`/update/${user._id}`} className="icon-btn-modern edit-btn" title="Edit">
                          <i className="bi bi-pencil-square"></i>
                        </Link>
                      )}
                      <button onClick={() => setDeleteModalUser(user._id)} className="icon-btn-modern delete-btn" title="Delete Options">
                        <i className="bi bi-trash3-fill"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PAGINATION */}
      {filteredUsers.length > rowsPerPage && (
        <div className="pagination-modern">
          <button className="btn outline-btn btn-sm" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
            <i className="bi bi-chevron-left me-1"></i> Prev
          </button>
          <span className="pagination-text">
            Showing <strong>{(currentPage - 1) * rowsPerPage + 1}</strong> to <strong>{Math.min(currentPage * rowsPerPage, filteredUsers.length)}</strong> of <strong>{filteredUsers.length}</strong> entries
          </span>
          <button className="btn outline-btn btn-sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
            Next <i className="bi bi-chevron-right ms-1"></i>
          </button>
        </div>
      )}

      {/* EXCEL PREVIEW MODAL */}
      {showPreview && (
        <div className="modal-overlay-modern">
          <div className="modal-card-modern">
            <div className="modal-header-modern">
              <h5><i className="bi bi-table me-2"></i> Excel Preview ({previewData.length} Records)</h5>
              <button className="btn-close-modern" onClick={() => setShowPreview(false)}><i className="bi bi-x-lg"></i></button>
            </div>
            
            {invalidCount > 0 ? (
              <div className="alert alert-danger modern-alert">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <strong>{invalidCount} invalid row(s) found.</strong> Please fix mandatory fields (Resource Name) and ensure Rate Card & CTC are > 0.
              </div>
            ) : previewData.length > 0 && (
              <div className="alert alert-success modern-alert">
                <i className="bi bi-check-circle-fill me-2"></i>
                All rows are valid. Ready to import.
              </div>
            )}

            <div className="table-scroll-wrapper preview-wrapper">
              <table className="resource-table preview-table">
                <thead>
                  <tr>
                    <th>Sl No</th>
                    <th>Resource Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                    <th>Domain</th>
                    <th>DOJ</th>
                    <th>DOJ Client</th>
                    <th>LWD</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, i) => {
                    const errors = getRowErrors(row);
                    const valid = errors.length === 0;
                    return (
                      <tr key={i} className={valid ? "" : "invalid-row"}>
                        <td>{i + 1}</td>
                        <td>{row.resourceName || "-"}</td>
                        <td>{row.email || "-"}</td>
                        <td>{row.mobile || "-"}</td>
                        <td>{row.domain || "-"}</td>
                        <td>{formatExcelDate(row.dojTapl) || "-"}</td>
                        <td>{formatExcelDate(row.dojE) || "-"}</td>
                        <td>{formatExcelDate(row.lastWorkingDay) || "-"}</td>
                        <td>
                          {valid ? (
                            <span className="badge bg-success badge-modern">Valid</span>
                          ) : (
                            <div>
                              <span className="badge bg-danger badge-modern">Invalid</span>
                              <ul className="error-list">
                                {errors.map((err, idx) => <li key={idx}>{err}</li>)}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div className="modal-footer-modern" style={{ padding: '20px 24px', backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="preview-summary" style={{ fontWeight: '500', color: '#475569' }}>
                Total Records: <span className="badge bg-primary rounded-pill">{previewData.length}</span>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button className="btn outline-btn" onClick={() => setShowPreview(false)}>Cancel</button>
                <button className="btn gradient-btn" disabled={invalidCount > 0} onClick={confirmImport}>
                  <i className="bi bi-cloud-arrow-up-fill me-2"></i> Confirm Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE OPTIONS MODAL */}
      {deleteModalUser && (
        <div className="modal-overlay-modern">
          <div className="modal-card-modern" style={{ maxWidth: '400px' }}>
            <div className="modal-header-modern">
              <h5 className="text-danger"><i className="bi bi-exclamation-triangle-fill me-2"></i> Delete Options</h5>
              <button className="btn-close-modern" onClick={() => setDeleteModalUser(null)}><i className="bi bi-x-lg"></i></button>
            </div>
            
            <div className="p-4 text-center">
              <p className="mb-4 text-muted">Please select how you want to delete this record.</p>
              
              <div className="d-flex flex-column gap-3">
                <button 
                  className="btn outline-btn w-100 text-warning border-warning"
                  onClick={() => deleteUser(deleteModalUser)}
                >
                  <i className="bi bi-archive-fill me-2"></i> Temporary Delete
                </button>
                
                <button 
                  className="btn btn-danger w-100"
                  onClick={() => deletePermanentUser(deleteModalUser)}
                >
                  <i className="bi bi-trash3-fill me-2"></i> Permanent Delete
                </button>
              </div>
            </div>
            
            <div className="modal-footer-modern justify-content-center">
              <button className="btn outline-btn" onClick={() => setDeleteModalUser(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* BULK DELETE MODAL */}
      {showBulkDeleteModal && (
        <div className="modal-overlay-modern">
          <div className="modal-card-modern" style={{ maxWidth: '450px' }}>
            <div className="modal-header-modern">
              <h5 className="text-danger"><i className="bi bi-exclamation-triangle-fill me-2"></i> Confirm Bulk Delete</h5>
              <button className="btn-close-modern" onClick={() => setShowBulkDeleteModal(false)}><i className="bi bi-x-lg"></i></button>
            </div>
            
            <div className="p-4 text-center">
              <p className="mb-4 text-muted">Are you sure you want to permanently delete <strong>{selectedIds.length}</strong> records? This action cannot be undone.</p>
              
              <div className="d-flex justify-content-center gap-3">
                <button className="btn outline-btn" onClick={() => setShowBulkDeleteModal(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={confirmBulkDelete}>
                  <i className="bi bi-trash3-fill me-2"></i> Confirm Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default User;
